import { difference, uniq } from 'lodash';
import * as Yup from 'yup';

const SensorRef = /sensors\['([^']+)'\]/g;

/**
 * Returns TRUE if the code's referenced sensors are actually defined or if
 * there are no referenced sensors.
 */
function areSensorsRefsValid(value, sensors) {
    if (!value) {
        return true;
    }
    const referencedSensorIds =
        uniq(
            Array.from(
                value.matchAll(SensorRef)
            ).map(([match, group]) => group)
        );
    if (!referencedSensorIds.length) {
        return true;
    }
    const sensorIds = (sensors || []).map(({ id }) => id);
    return difference(referencedSensorIds, sensorIds).length === 0;
}

/**
 * Validates that an enabled rule's sensor references are valid.
 */
Yup.addMethod(
    Yup.string,
    'ensureSensorsRefs',
    function ensureSensorsRefsValidator(formats, parseStrict) {
        return this.when('enabled', (enabled, schema) => {
            if (!enabled) {
                return schema;
            }
            return schema.when('sensors', (sensors, schema) => {
                return schema.test({
                    name:    'ensure-sensors-refs',
                    message: 'Reference to non-existent sensor found. Either re-add the sensor, choose a different sensor, or disable the rule.',
                    test(value, schema) {
                        return areSensorsRefsValid(value, sensors);
                    }
                });
            });
        });
    }
);

/**
 * Validates that the property is required when the rule is enabled.
 */
Yup.addMethod(
    Yup.string,
    'requiredWhenEnabled',
    function requiredWhenEnabledValidator(formats, parseStrict) {
        return this.when('enabled', (enabled, schema) => {
            if (!enabled) {
                return schema;
            }
            return schema.required();
        });
    }
);

const RequiredAggregateDurationTypes = [
    'ChangeCount',
    'PercentActive',
    'TotalDuration'
];

const ModifyRulePageSchema = Yup.object().shape({
    ruleAggregateDuration: Yup.number()
        .nullable()
        // Required when :aggregateType is one of a subset of values
        .when('ruleAggregateType', (ruleAggregateType, schema) => {
            if (!RequiredAggregateDurationTypes.includes(ruleAggregateType)) {
                return schema;
            }
            return schema.required();
        }),
    ruleAggregateType:  Yup.string().required(),
    ruleAggregateValue: Yup.number()
        .nullable()
        // Required when any :aggregateType value is set
        .when('ruleAggregateType', (ruleAggregateType, schema) => {
            if (!ruleAggregateType || ruleAggregateType === 'None') {
                return schema;
            }
            let typeSchema = schema.required();
            if (ruleAggregateType === 'PercentActive') {
                typeSchema = typeSchema.max(100).min(0);
            }
            if (ruleAggregateType === 'TotalDuration') {
                typeSchema = typeSchema.max(
                    Yup.ref('ruleAggregateDuration'),
                    'This value must be less than or equal to Aggregate Duration'
                );
            }
            return typeSchema;
        }),
    enabled:        Yup.boolean(),
    faultLogicCode: Yup.string().requiredWhenEnabled().ensureSensorsRefs(),
    formulaCode:    Yup.string().requiredWhenEnabled().ensureSensorsRefs(),
    name:           Yup.string().required(),
    priority:       Yup.number().nullable().required(),
    sensors:        Yup.mixed(),
    target:         Yup.string()
        .nullable()
        .oneOf(
            ['Equipment', 'System'],
            'You must choose either a System or Equipment target.'
        )

});

export default ModifyRulePageSchema;
